import { Icon } from "@iconify/react";
import {
  Button,
  Card,
  Flex,
  Grid,
  Image,
  Input,
  message,
  Modal,
  Pagination,
  Select,
  Table,
  Tag,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { announcementService } from "../../services/announcement.service";
import useTitleStore from "../../store/titleStore";
import { useTableHeight } from "../../hooks/useTableHeight";
import useDebounce from "../../hooks/useDebounce";
import TableAction from "../../components/TableAction";
import UpdatedByCell from "../../components/UpdatedByCell";

const categoryColorMap = {
  info: "blue",
  penting: "red",
  pemberitahuan: "orange",
  kegiatan: "purple",
};

const categoryLabelMap = {
  info: "Informasi",
  penting: "Penting",
  pemberitahuan: "Pemberitahuan",
  kegiatan: "Kegiatan",
};

export default function AnnouncementIndex() {
  const breakpoint = Grid.useBreakpoint();
  const navigate = useNavigate();
  const { setTitle } = useTitleStore();
  const queryClient = useQueryClient();
  const tableHeight = useTableHeight(420);

  const [messageApi, contextHolder] = message.useMessage();
  const [keyword, setKeyword] = useState("");
  const debouncedKeyword = useDebounce(keyword, 500);

  const [selectedCategory, setSelectedCategory] = useState(undefined);
  const [selectedStatus, setSelectedStatus] = useState(undefined);

  const [previewItem, setPreviewItem] = useState(null);

  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    keyword: null,
    category: undefined,
    isActive: undefined,
  });

  const {
    data: announcementData,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: ["announcement", dataParams],
    queryFn: () => announcementService.fetchAll(dataParams),
    placeholderData: keepPreviousData,
  });

  const { mutate: handleDelete, isPending: deleteLoading } = useMutation({
    mutationFn: (uuid) => announcementService.delete(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcement"] });
      messageApi.success("Pengumuman berhasil dihapus");
    },
    onError: (error) => {
      console.error(error);
      messageApi.error("Terjadi kesalahan saat menghapus pengumuman");
    },
  });

  useEffect(() => {
    setTitle("Pengumuman");
  }, [setTitle]);

  useEffect(() => {
    setDataParams((prev) => ({
      ...prev,
      page: 1,
      keyword: debouncedKeyword || undefined,
      category: selectedCategory,
      isActive: selectedStatus,
    }));
  }, [debouncedKeyword, selectedCategory, selectedStatus]);

  const handleChangePage = (page, pageSize) => {
    setDataParams((prev) => ({
      ...prev,
      limit: pageSize,
      page,
    }));
  };

  const columns = [
    {
      title: "No",
      align: "center",
      width: 50,
      render: (text, record, index) =>
        ((announcementData?.pagination?.page || 1) - 1) * dataParams.limit +
        index +
        1,
      fixed: breakpoint.md && "left",
    },
    {
      title: "Banner",
      align: "center",
      width: 90,
      render: (record) =>
        record.banner ? (
          <Image
            src={record.banner}
            alt={record.title}
            width={54}
            height={38}
            style={{
              objectFit: "cover",
              borderRadius: 6,
              border: "1px solid #e2e8f0",
            }}
            preview={{ mask: <Icon icon="solar:eye-bold" width="16" /> }}
          />
        ) : (
          <Flex
            align="center"
            justify="center"
            style={{
              width: 54,
              height: 38,
              backgroundColor: "#f1f5f9",
              borderRadius: 6,
              color: "#94a3b8",
              margin: "0 auto",
            }}
          >
            <Icon icon="solar:gallery-wide-linear" width="20" />
          </Flex>
        ),
    },
    {
      title: "Judul Pengumuman",
      dataIndex: "title",
      width: 220,
      fixed: breakpoint.md && "left",
      render: (val, record) => (
        <Flex vertical gap={4}>
          <Flex align="center" gap={6}>
            {record.isPinned && (
              <Tag
                color="gold"
                style={{
                  margin: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  fontSize: 11,
                  padding: "0 6px",
                }}
              >
                <Icon icon="solar:pin-bold" width="12" height="12" />
                Pin
              </Tag>
            )}
            <Typography.Text
              style={{
                fontWeight: 600,
                cursor: "pointer",
              }}
              onClick={() => setPreviewItem(record)}
            >
              {val}
            </Typography.Text>
          </Flex>
          <Typography.Paragraph
            ellipsis={{ rows: 1 }}
            type="secondary"
            style={{ margin: 0, fontSize: 12 }}
          >
            {record.content?.replace(/<[^>]*>/g, "")}
          </Typography.Paragraph>
        </Flex>
      ),
    },
    {
      title: "Kategori",
      dataIndex: "category",
      width: 120,
      render: (val) => {
        const color = categoryColorMap[val?.toLowerCase()] || "default";
        const label = categoryLabelMap[val?.toLowerCase()] || val || "-";
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      width: 100,
      render: (val) =>
        val ? (
          <Tag color="green">Aktif</Tag>
        ) : (
          <Tag color="red">Tidak Aktif</Tag>
        ),
    },
    {
      title: "Direktorat",
      dataIndex: "directorate",
      width: 140,
      render: (val) => val ?? "-",
    },
    {
      title: "Update By",
      width: 140,
      render: (record) => <UpdatedByCell value={record} />,
    },
    {
      className: "last-cell-p",
      title: "Aksi",
      align: "right",
      width: 80,
      fixed: "right",
      render: (record) => (
        <TableAction
          onClickEdit={() => navigate(`/announcement/detail/${record.uuid}`)}
          handleDelete={() => handleDelete(record.uuid)}
          deleteLoading={deleteLoading}
        />
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Card
        styles={{
          body: {
            paddingLeft: 0,
            paddingRight: 0,
          },
        }}
      >
        <Flex
          justify="space-between"
          align="center"
          wrap="wrap"
          gap={12}
          style={{ paddingInline: 24, marginBottom: 20 }}
        >
          <Input
            style={{ width: 280 }}
            prefix={<Icon icon="hugeicons:search-01" width="16" height="16" />}
            placeholder="Cari judul pengumuman..."
            onChange={(e) => setKeyword(e.target.value)}
            value={keyword}
            allowClear
          />

          <Flex wrap="wrap" gap={12} align="center">
            <Select
              placeholder="Filter Kategori"
              style={{ width: 160 }}
              allowClear
              value={selectedCategory}
              onChange={(val) => setSelectedCategory(val)}
              options={[
                { label: "Informasi", value: "info" },
                { label: "Penting", value: "penting" },
                { label: "Pemberitahuan", value: "pemberitahuan" },
                { label: "Kegiatan", value: "kegiatan" },
              ]}
            />
            <Select
              placeholder="Filter Status"
              style={{ width: 140 }}
              allowClear
              value={selectedStatus}
              onChange={(val) => setSelectedStatus(val)}
              options={[
                { label: "Aktif", value: "true" },
                { label: "Tidak Aktif", value: "false" },
              ]}
            />
            <Button
              variant="solid"
              color="primary"
              icon={<Icon icon="duo-icons:add-circle" width="22" height="22" />}
              onClick={() => navigate("/announcement/create")}
              style={{ minWidth: 38 }}
            >
              {breakpoint.md && "Tambah Pengumuman"}
            </Button>
          </Flex>
        </Flex>

        <Table
          columns={columns}
          pagination={false}
          size="small"
          loading={isLoading || isRefetching}
          rowKey={(record) => record.uuid || record.id}
          scroll={{ y: tableHeight, x: 1050 }}
          dataSource={announcementData?.data || []}
        />

        <Pagination
          style={{ marginInline: 24, marginTop: 16 }}
          total={announcementData?.pagination?.total || 0}
          showSizeChanger={{
            showSearch: false,
          }}
          pageSizeOptions={["10", "20", "50"]}
          current={announcementData?.pagination?.page || 1}
          pageSize={dataParams.limit}
          align="end"
          onChange={handleChangePage}
        />
      </Card>

      {/* Detail Preview Modal */}
      <Modal
        open={!!previewItem}
        onCancel={() => setPreviewItem(null)}
        footer={[
          <Button key="close" onClick={() => setPreviewItem(null)}>
            Tutup
          </Button>,
          <Button
            key="edit"
            type="primary"
            icon={<Icon icon="solar:pen-2-bold-duotone" width="16" />}
            onClick={() => {
              const uuid = previewItem?.uuid;
              setPreviewItem(null);
              navigate(`/announcement/detail/${uuid}`);
            }}
          >
            Ubah
          </Button>,
        ]}
        title="Detail Pengumuman"
        width={650}
      >
        {previewItem && (
          <Flex vertical gap={16} style={{ paddingTop: 8 }}>
            {previewItem.banner && (
              <Image
                src={previewItem.banner}
                alt={previewItem.title}
                style={{
                  width: "100%",
                  maxHeight: 260,
                  objectFit: "cover",
                  borderRadius: 8,
                }}
              />
            )}
            <Flex align="center" gap={8} wrap="wrap">
              <Tag
                color={
                  categoryColorMap[previewItem.category?.toLowerCase()] ||
                  "default"
                }
              >
                {categoryLabelMap[previewItem.category?.toLowerCase()] ||
                  previewItem.category}
              </Tag>
              {previewItem.isPinned && (
                <Tag color="gold" icon={<Icon icon="solar:pin-bold" />}>
                  Disematkan
                </Tag>
              )}
              {previewItem.isActive ? (
                <Tag color="green">Aktif</Tag>
              ) : (
                <Tag color="red">Tidak Aktif</Tag>
              )}
              {previewItem.directorate && (
                <Tag color="cyan">{previewItem.directorate}</Tag>
              )}
            </Flex>

            <Typography.Title level={4} style={{ margin: 0 }}>
              {previewItem.title}
            </Typography.Title>

            <div
              style={{
                whiteSpace: "pre-wrap",
                fontSize: 14,
                lineHeight: 1.6,
                color: "#334155",
                backgroundColor: "#f8fafc",
                padding: 16,
                borderRadius: 8,
                border: "1px solid #e2e8f0",
              }}
            >
              {previewItem.content}
            </div>
          </Flex>
        )}
      </Modal>
    </>
  );
}
